import { create } from "zustand";

/**
 * État de mise en page de la barre latérale gauche (`AppSidebar`) : largeur
 * redimensionnable + état replié, persistés en localStorage (pas de backend).
 * Consommé à la fois par `AppSidebar` (sa propre largeur, le bouton replier et la
 * poignée de drag) et par `DashboardLayout` (la marge gauche du contenu suit la
 * largeur). L'état « en cours de redimensionnement » reste local à `DashboardLayout`
 * (transitoire, non persisté) — il sert juste à couper la transition de marge
 * pendant le drag pour que le contenu colle au curseur.
 *
 * **Store zustand (2026-07-16)** — était un hook `useState` local ; migré vers
 * un store global pour qu'un composant PROFOND dans l'arbre (`AgentV3View`)
 * puisse replier la barre à l'ouverture du canvas de rédaction (`setCollapsed`)
 * sans prop-drilling depuis `DashboardLayout`. L'API publique
 * (`{ width, collapsed, setWidth, toggleCollapsed }`) est inchangée — seul
 * `setCollapsed` est ajouté. `useSidebarLayout()` (sans selector) renvoie tout
 * le state comme avant.
 */
export const SIDEBAR_MIN_WIDTH = 208;
export const SIDEBAR_MAX_WIDTH = 440;
export const SIDEBAR_DEFAULT_WIDTH = 256; // = ancien w-64
/** Largeur du rail replié (icônes seules). */
export const SIDEBAR_COLLAPSED_WIDTH = 56;

const WIDTH_KEY = "hector-sidebar-width";
const COLLAPSED_KEY = "hector-sidebar-collapsed";

const clampWidth = (w: number): number =>
  Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, Math.round(w)));

function readInitialWidth(): number {
  try {
    const stored = localStorage.getItem(WIDTH_KEY);
    return stored ? clampWidth(parseInt(stored, 10)) : SIDEBAR_DEFAULT_WIDTH;
  } catch {
    return SIDEBAR_DEFAULT_WIDTH;
  }
}

function readInitialCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
}

function persist(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* localStorage indisponible (mode privé) — on garde l'état en mémoire. */
  }
}

interface SidebarLayout {
  /** Largeur courante (px) quand la barre est dépliée. */
  width: number;
  /** Barre repliée (cachée) ou non. */
  collapsed: boolean;
  /** Pose la largeur (bornée min/max) + persiste. */
  setWidth: (width: number) => void;
  /** Bascule replié / déplié + persiste. */
  toggleCollapsed: () => void;
  /**
   * Pose l'état replié explicitement (idempotent) + persiste. Permet à
   * `AgentV3View` de replier la barre à l'ouverture du canvas sans forcer un
   * état permanent — l'utilisateur peut la rouvrir avec `toggleCollapsed`.
   */
  setCollapsed: (collapsed: boolean) => void;
}

export const useSidebarLayout = create<SidebarLayout>((set, get) => ({
  width: readInitialWidth(),
  collapsed: readInitialCollapsed(),
  setWidth: (w) => {
    const clamped = clampWidth(w);
    set({ width: clamped });
    persist(WIDTH_KEY, String(clamped));
  },
  toggleCollapsed: () => {
    const next = !get().collapsed;
    set({ collapsed: next });
    persist(COLLAPSED_KEY, String(next));
  },
  setCollapsed: (collapsed) => {
    if (get().collapsed === collapsed) return;
    set({ collapsed });
    persist(COLLAPSED_KEY, String(collapsed));
  },
}));
